import { NetworkStatus } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import {
  EnumNotificationTechnology,
  TNotificationFieldsFragment,
  useGetUserNotificationsConnectionQuery,
  useRemoveNotificationMutation,
  useUpdateNotificationMutation,
} from 'generated/graphql';
import { useSession } from 'providers';
import { useScrollObserver } from 'utils/hooks/useScrollObserver';
import { logError } from 'utils/logError';
import { Technology } from 'utils/products';
import { isSessionExpired } from 'utils/sessionExpiry';
import { GET_USER_UNREAD_NOTIFICATIONS_COUNT } from '../notifications.api';

const PAGE_SIZE = 20;

// Start fetching before reaching the last notification. For example,
// if 5 it will fetch when reaching the 5th before last notification.
const PREFETCH_OFFSET = 5;

export const useNotifications = ({
  areNotificationsOpen,
}: {
  areNotificationsOpen: boolean;
}) => {
  const [currentNotifications, setCurrentNotifications] = useState<
    TNotificationFieldsFragment[]
  >([]);
  const observerItem = useRef<IntersectionObserver | null>(null);
  const { getExpiresIn, firebaseId } = useSession();

  const isSessionValid =
    !isSessionExpired(getExpiresIn()) && Boolean(firebaseId);
  const technology =
    Technology.availableTechnology as EnumNotificationTechnology;

  const { data, loading, networkStatus, fetchMore, refetch, client } =
    useGetUserNotificationsConnectionQuery({
      skip: !isSessionValid || !areNotificationsOpen,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: { first: PAGE_SIZE, technology },
      onError: (error) => {
        logError({ error });
      },
    });

  // Clear the cache so we don't need to worry about stale data in notifications that
  // were fetched via "fetchMore"
  useEffect(() => {
    if (!areNotificationsOpen) {
      client.cache.evict({ fieldName: 'userNotificationsConnection' });
      client.cache.gc();
    } else {
      setCurrentNotifications([]);
    }
  }, [areNotificationsOpen, client]);

  useEffect(() => {
    if (!data) return;

    const notifications =
      data.userNotificationsConnection?.edges?.flatMap((edge) =>
        edge?.node ? [edge.node] : [],
      ) ?? [];
    setCurrentNotifications(notifications);
  }, [data]);

  const fetchMoreNotifications = () => {
    const pageInfo = data?.userNotificationsConnection?.pageInfo;
    if (pageInfo?.hasNextPage) {
      void fetchMore({
        variables: { after: pageInfo.endCursor, first: PAGE_SIZE, technology },
      });
    }
  };

  const handleRefreshNotifications = () => {
    void refetch({ first: PAGE_SIZE, technology });
  };

  const getSentinelRef = useScrollObserver<HTMLLIElement>({
    isLoading: loading,
    observerItem,
    onIntersection: fetchMoreNotifications,
  });

  const [updateNotification] = useUpdateNotificationMutation({
    refetchQueries: [GET_USER_UNREAD_NOTIFICATIONS_COUNT],
    onCompleted: (response) => {
      const _id = response.updateNotification?.record?._id;
      setCurrentNotifications((prev) =>
        prev.map((n) => (n._id === _id ? { ...n, read: true } : n)),
      );
    },
  });

  const [removeNotification] = useRemoveNotificationMutation({
    refetchQueries: [GET_USER_UNREAD_NOTIFICATIONS_COUNT],
    onCompleted: (response) => {
      const _id = response.removeNotification?.record?._id;
      setCurrentNotifications((prev) => prev.filter((n) => n._id !== _id));
    },
    // Remove the notification from the cache
    update: (cache, { data: responseData }) => {
      const deletedId = responseData?.removeNotification?.record?._id;
      if (deletedId) {
        cache.evict({
          id: cache.identify({ __typename: 'Notification', _id: deletedId }),
        });
        cache.gc();
      }
    },
  });

  const count = currentNotifications.length;
  const sentinelIndex =
    count > PREFETCH_OFFSET ? count - PREFETCH_OFFSET - 1 : count - 1;

  return {
    handleRefreshNotifications,
    notifications: currentNotifications,
    isLoadingNotifications: loading && count === 0,
    updateNotification,
    removeNotification,
    getSentinelRef,
    sentinelIndex,
    isFetchingMore: networkStatus === NetworkStatus.fetchMore,
  };
};
